import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import formConfig from './formConfig';
import TabNavigation from './TabNavigation';
import FormTab from './FormTab';
import ReviewTab from './ReviewTab';
import './styles.css';
import { useNavigate } from "react-router-dom";
import { createPatientDetails, validateEmailInDatabase } from '../../Services/APIs/patient';
import { postLogDetails } from '../../Services/APIs/appDetails';
import { useDispatch } from "react-redux";
import { addPatientData } from "../../Store/patientKyc";
import { useFormPersistence } from '../../hooks/useFormPersistence';

const generateInitialValues = () => {
  const initial = {};
  formConfig.tabs.forEach((tab) => {
    tab.fields.forEach((field) => {
      if (field.type === 'checkbox') {
        initial[field.name] = false;
      } else if (field.type === 'multiselect') {
        initial[field.name] = [];
      }
      else {
        initial[field.name] = '';
      }
    });
  });
  return initial;
};


const GroupInsuranceForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [validationSchemas, setValidationSchemas] = useState([]);
  const [validationError, setValidationError] = useState(false);
  const [isEmailValidated, setIsEmailValidated] = useState(false);
  const [emailValidationLoading, setEmailValidationLoading] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [employeeData, setEmployeeData] = useState(null);
  const [readOnlyFields, setReadOnlyFields] = useState([]);
  const navigate = useNavigate()
  const [navigateTo, setNavigateTo] = useState(null);
  const dispatch = useDispatch()
  const validationAttemptedRef = useRef(false)
  
  // Use form persistence hook
  const { formData, setFormData, clearFormData } = useFormPersistence(generateInitialValues())

  // Scroll to top when active tab changes
  useEffect(() => {
    // Immediate scroll without smooth behavior for reliability
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Also scroll the main form container if it exists
    const formContainer = document.querySelector('.dental-insurance-form');
    if (formContainer) {
      formContainer.scrollTop = 0;
    }
  }, [activeTab]);

  // Convert an ISO UTC date string to an IST yyyy-MM-dd string (matches the
  // value format used by the `date` input so it submits consistently).
  const toISTDateString = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  };

  // Pre-populate the personal info fields from the validated employee record and
  // lock (read-only) every field we were able to fill. Fields without data stay
  // editable so the user can type them in.
  const prefillFromEmployeeData = useCallback((data) => {
    if (!data) return;
    const updates = {};
    const locked = [];

    if (data.employee_mail) {
      updates.email = data.employee_mail;
      locked.push('email');
    }

    const mobile = (data.phone_number || '').replace(/\D/g, '').slice(-10);
    if (mobile) {
      updates.mobile = mobile;
      locked.push('mobile');
    }

    const selectedUid = JSON.parse(localStorage.getItem('usertpi') || '{}')?.usertpi;
    const ben = data.beneficiaries;
    if (ben && Array.isArray(ben.uids) && selectedUid) {
      const idx = ben.uids.indexOf(selectedUid);
      if (idx !== -1) {
        if (ben.names?.[idx]) {
          updates.customerName = ben.names[idx];
          locked.push('customerName');
        }
        if (ben.relationships?.[idx]) {
          updates.relationToCustomer = ben.relationships[idx];
          locked.push('relationToCustomer');
        }
        if (ben.genders?.[idx]) {
          updates.customerGender = ben.genders[idx];
          locked.push('customerGender');
        }
        const dob = toISTDateString(ben.dobs?.[idx]);
        if (dob) {
          updates.dob = dob;
          locked.push('dob');
        }
      }
    }

    setFormData(prev => ({ ...prev, ...updates }));
    setReadOnlyFields(locked);
  }, [setFormData]);

  const handleEmailValidation = useCallback(async (email) => {
    // Prevent multiple validations
    if (validationAttemptedRef.current) {
      return;
    }
    
    if (!email || !email.trim()) {
      setEmailValidationError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailValidationError('Please enter a valid email address');
      return;
    }

    validationAttemptedRef.current = true;
    setEmailValidationLoading(true);
    setEmailValidationError('');

    // Check for demo email - bypass API call and proceed with positive flow
    if (email.toLowerCase() === 'demo@toothlens.com') {
      // Simulate loading
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setIsEmailValidated(true);
      setUserEmail(email);
      const demoEmployeeData = {
        employee_mail: email,
        members_allowed: 5,
        company_name: 'Demo Company'
      };
      setEmployeeData(demoEmployeeData);
      localStorage.setItem('employeeData', JSON.stringify(demoEmployeeData));
      setFormData(prev => ({ ...prev, email: email }));
      setEmailValidationLoading(false);
      return;
    }

    try {
      // Call the API function to validate email in database
      const response = await validateEmailInDatabase(email);
      
      // Check if response is successful and has data
      if (response && response.data && response.data.code === 200) {
        const apiEmail = response.data.data.employee_mail;
        const membersAllowed = response.data.data.members_allowed;
        
        // Check if members_allowed is 0
        if (membersAllowed === 0) {
          setEmailValidationError('All members are now successfully registered, and no new members can be added at this time.');
          postLogDetails({
            uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
            app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
            payLoad: `email validation failed - all members registered: ${email}`
          });
          return;
        }
        // Compare input email with API response email
        if (email.toLowerCase().trim() === apiEmail.toLowerCase().trim()) {
          setIsEmailValidated(true);
          setUserEmail(email);
          setEmployeeData(response.data.data); // Store the employee data
          // Store employee data in localStorage for access in other components
          localStorage.setItem('employeeData', JSON.stringify(response.data.data));
          // Store max_member_allowed separately in localStorage
          if (response.data.data.max_member_allowed !== undefined) {
            localStorage.setItem('MaxMemberAllowed', response.data.data.max_member_allowed);
          }
          // Pre-populate and lock the selected member's personal info
          prefillFromEmployeeData(response.data.data);
          postLogDetails({
            uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
            app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
            payLoad: `email validation successful: ${email}`
          });
        } else {
          setEmailValidationError('Email mismatch. Please enter the correct registered email address.');
          postLogDetails({
            uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
            app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
            payLoad: `email validation failed - mismatch: ${email}`
          });
        }
      } else {
        setEmailValidationError(response?.data?.message || 'Email not found in our database. Please contact support or use a registered email address.');
        postLogDetails({
          uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
          app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
          payLoad: `email validation failed: ${email}`
        });
      }
    } catch (error) {
      setEmailValidationError('Unable to validate email. Please try again or contact support.');
      postLogDetails({
        uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
        app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
        payLoad: `email validation error: ${email}`
      });
    } finally {
      setEmailValidationLoading(false);
    }
  }, [prefillFromEmployeeData, setFormData]);

  const EmailValidationStep = () => {
    const [email, setEmail] = useState('');
    const hasValidatedRef = useRef(false);

    useEffect(() => {
      if (hasValidatedRef.current) return;
      
      const storedEmail = localStorage.getItem('insuranceLoginEmail');
      if (storedEmail) {
        hasValidatedRef.current = true;
        setEmail(storedEmail);
        handleEmailValidation(storedEmail);
      }
    }, [handleEmailValidation]);

    return (
      <div className="email-validation-container">
        {emailValidationLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-center">
              <svg className="loading-spinner w-16 h-16 mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-lg font-semibold text-gray-700">Validating...</p>
              <p className="text-sm text-gray-500 mt-2">Please wait while we verify your email</p>
            </div>
          </div>
        )}
        
        {/* Header with logo/icon */}
        <div className="text-center mb-8">
          <div className="email-icon-wrapper mb-4">
            <svg className="email-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verification</h2>
          {/* <p className="text-gray-600">Please enter your registered email address to continue with your dental insurance form</p> */}
        </div>
        
        <div className="email-form-wrapper">
          {emailValidationError && (
            <div className="error-message">
              <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <p className="error-text">{emailValidationError}</p>
            </div>
          )}
          
          <div className="back-link-wrapper">
            <button
              onClick={() => navigate(-1)}
              className="back-link"
            >
              ← Back to previous page
            </button>
          </div>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // postLogDetails({
    //   uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
    //   app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
    //   payLoad: `form submitted: ${navigateTo}`

    // })
    if (navigateTo) {
      // postLogDetails({
      //   uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
      //   app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
      //   payLoad: `form submitted: ${navigateTo}`

      // })
      navigate(navigateTo);
    }
  }, [navigateTo]);
  useEffect(() => {
    const schemas = formConfig.tabs.map(tab => {
      const schemaObj = {};
      tab.fields.forEach(field => {
        // Skip validation if field has conditionalRender and it returns false
        if (field.conditionalRender && typeof field.conditionalRender === 'function') {
          if (!field.conditionalRender()) {
            return;
          }
        }

        if (field.required) {
          if (field.type === 'text' || field.type === 'textarea') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'email') {
            schemaObj[field.name] = Yup.string().email('Invalid email format').required(`${field.label} is required`);
          } else if (field.type === 'date') {
            schemaObj[field.name] = Yup.date().required(`${field.label} is required`);
          } else if (field.type === 'checkbox') {
            schemaObj[field.name] = Yup.boolean().oneOf([true], `${field.label} must be accepted`);
          } else if (field.type === 'radio' || field.type === 'select') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'fileUpload') {
            schemaObj[field.name] = Yup.string().required(`${field.label} is required`);
          } else if (field.type === 'multiselect') {
            schemaObj[field.name] = Yup.array().min(1, `${field.label} is required`).required(`${field.label} is required`);
          } else if (field.name === 'declarationAgreement') {
            schemaObj[field.name] = Yup.boolean().oneOf([true], 'You must accept the declaration to proceed');
          } else if (field.type === 'number') {
            schemaObj[field.name] = Yup.number()
              .typeError(`${field.label} must be a number`)
              .required(`${field.label} is required`)
              .test(
                'len',
                `${field.label} must be at most 10 digits`,
                (val) => val === undefined || val.toString().length <= 10
              );
          }



        }
      });
      return Yup.object().shape(schemaObj);
    });
    setValidationSchemas(schemas);
  }, []);

  const handleNextTab = (values, actions) => {
    actions.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0) {
        setFormData({ ...formData, ...values });
        if (activeTab < formConfig.tabs.length - 1) {
          setActiveTab(activeTab + 1);
          setValidationError(false);
        }
        actions.setSubmitting(false);
      } else {
        const touchedFields = {};
        Object.keys(errors).forEach((field) => {
          touchedFields[field] = true;
        });
        actions.setTouched(touchedFields);
        setValidationError(true);
        actions.setSubmitting(false);
      }
    });
  };

  const handlePrevTab = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
      setValidationError(false);
    }
  };
  // Utility: Convert base64 string to File
  const base64ToFile = (base64Data, fileName, mimeType = "image/jpeg") => {
    const byteString = atob(base64Data.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new File([ab], fileName, { type: mimeType });
  };

  const handleSubmit = async (values) => {
    // console.log(values, "submitted values");

    const { aadhaarCardBack, aadhaarCard, panCard, ...restValues } = values;
    const formData = new FormData();
    // console.log(typeof aadhaarCard === "string", aadhaarCard.startsWith("data:image"))
    // Convert base64 to File and append
    if (typeof aadhaarCard === "string" && aadhaarCard.startsWith("data:image")) {
      const aadhaarFile = base64ToFile(aadhaarCard, "aadhaar.jpg");
      formData.append("aadhaarCard", aadhaarFile);
    }

    if (typeof aadhaarCardBack === "string" && aadhaarCardBack.startsWith("data:image")) {
      const aadhaarBackFile = base64ToFile(aadhaarCardBack, "aadhaar_back.jpg");
      formData.append("aadhaarCardBack", aadhaarBackFile);
    }

    if (typeof panCard === "string" && panCard.startsWith("data:image")) {
      const panFile = base64ToFile(panCard, "pan.jpg");
      formData.append("panCard", panFile);
    }

    // Append all other form fields
    for (const key in restValues) {
      if (restValues[key] !== undefined && restValues[key] !== null) {
        formData.append(key, restValues[key]);
      }
    }

    // Add UID and company from localStorage
    const uid = JSON.parse(localStorage.getItem("usertpi"))?.usertpi;
    const company = JSON.parse(localStorage.getItem("companyBucket"))?.companyBucket;
    formData.append("uid", uid);
    formData.append("company", company);

    // Optional logging
    // console.log([...formData.entries()]);
    // for (const [key, value] of formData.entries()) {
    //   console.log(`${key}:`, value);
    // }
    
    // Check for demo email - bypass API calls
    const storedEmail = localStorage.getItem('insuranceLoginEmail');
    const isDemoUser = storedEmail && storedEmail.toLowerCase() === 'demo@toothlens.com';
    
    try {
      // await createPatientDetails(formData); // Must accept FormData (multipart/form-data)
      dispatch(addPatientData(values));
      
      if (!isDemoUser) {
        postLogDetails({
          uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
          app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
          payLoad: `patient kyc state updated success`
        })
      }
    } catch (err) {
      if (!isDemoUser) {
        postLogDetails({
          uid: JSON.parse(localStorage.getItem("usertpi")).usertpi,
          app: JSON.parse(localStorage.getItem("companyBucket")).companyBucket,
          payLoad: `patient kyc state updated failure`
        })
      }
    }
    const cameraside = localStorage.getItem("cameraSide")
    const companyBucketMap = {
      "ab": "ab", // "aditya-birlaBucket",
      "tl-star": "starhealth",     // "Starhealth"
      "care": "care",  //Care Insurance
     
    };
    const validCameraSides = ["front", "back"];
    const resolvedCameraSide = validCameraSides.includes(cameraside) ? cameraside : "front";
    const resolvedCompany = companyBucketMap.hasOwnProperty(company) ? company : "tl-star";

    navigate(`/group/${resolvedCompany}/${resolvedCameraSide}/scan`)

  };




  const isLastTab = activeTab === formConfig.tabs.length - 1;
  const isReviewTab = activeTab === formConfig.tabs.length - 1;

  // Show email validation step first
  if (!isEmailValidated) {
    return (
      <div className="dental-insurance-form">
        <EmailValidationStep />
      </div>
    );
  }

  return (
    <div className="dental-insurance-form">

      {validationError && (
        <div className="validation-error-message">
          Please fill all required fields before proceeding to the next step.
        </div>
      )}

      <Formik
        initialValues={formData}
        validationSchema={validationSchemas[activeTab]}
        onSubmit={isLastTab ? handleSubmit : handleNextTab}
        enableReinitialize
        validateOnChange
        validateOnBlur
      >
        {(formikProps) => {
          const {
            values,
            setFieldValue,
            errors,
            touched,
            validateForm,
            setTouched,
            isSubmitting
          } = formikProps;

          return (
            <>
              <Form className="form-container">
                {isReviewTab ? (
                  <ReviewTab
                    formData={formData}
                    formConfig={formConfig}
                    {...formikProps}
                  />
                ) : (
                  <FormTab
                    key={activeTab}
                    tabConfig={formConfig.tabs[activeTab]}
                    formikProps={{ values, setFieldValue, errors, touched }}
                    employeeData={employeeData}
                    readOnlyFields={readOnlyFields}
                  />
                )}

                <div className="form-navigation">
                  {activeTab > 0 && (
                    <button
                      type="button"
                      className="btn-prev"
                      onClick={handlePrevTab}
                    >
                      Previous
                    </button>
                  )}

                  <button
                    type="submit"
                    className="btn-next"
                    disabled={isSubmitting}
                  >
                    {isLastTab ? 'Confirm' : 'Next'}
                  </button>
                </div>
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default GroupInsuranceForm;
